import "./timer.css";

import { DIAL, wedgePath } from "./timerDial";
import {
  deriveView,
  DURATION_CHIPS,
  INITIAL_TIMER,
  type TimerView,
} from "./timerState";

export interface TimerControls {
  durationSeconds: number;
  /** The duration being typed, or null when not editing. */
  draft?: string | null;
  onStart?: () => void;
  onPause?: () => void;
  onReset?: () => void;
  onAddMinute?: () => void;
  onChip?: (minutes: number) => void;
  onEditStart?: () => void;
  onDraftChange?: (value: string) => void;
  onDraftCommit?: () => void;
  onDraftCancel?: () => void;
}

const { cx, cy, r, band } = DIAL;

const TICKS = Array.from({ length: 12 }, (_, i) => {
  const a = (i / 12) * 2 * Math.PI;
  const quarter = i % 3 === 0;
  const r1 = r - 6;
  const r2 = r - (quarter ? 16 : 11);
  const at = (d: number) => ({
    x: +(cx + d * Math.sin(a)).toFixed(2),
    y: +(cy - d * Math.cos(a)).toFixed(2),
  });
  return { from: at(r1), to: at(r2), width: quarter ? 3.5 : 2 };
});

const BUTTON_ROTATE = `rotate(45 ${cx + r - 8} ${cy - r + 9})`;

function Silhouette({ className }: { className: string }) {
  return (
    <g
      className={className}
      transform={className === "tmr-shadow" ? "translate(4 6)" : undefined}
    >
      <circle cx={cx} cy={cy} r={r + 8} />
      <rect x={cx - 14} y={cy - r - 26} width={28} height={20} rx={5} />
      <rect
        x={cx + r - 18}
        y={cy - r + 2}
        width={20}
        height={14}
        rx={4}
        transform={BUTTON_ROTATE}
      />
      <rect {...band} rx={12} />
    </g>
  );
}

function Dial({ progress }: { progress: number }) {
  return (
    <svg
      width={DIAL.width}
      height={DIAL.height}
      viewBox={`0 0 ${DIAL.width} ${DIAL.height}`}
      aria-hidden
    >
      <Silhouette className="tmr-shadow" />
      <Silhouette className="tmr-cut" />
      <rect
        className="tmr-ink"
        x={cx - 14}
        y={cy - r - 26}
        width={28}
        height={20}
        rx={5}
      />
      <rect
        className="tmr-gold"
        x={cx - 20}
        y={cy - r - 12}
        width={40}
        height={10}
        rx={4}
      />
      <rect
        className="tmr-ink"
        x={cx + r - 18}
        y={cy - r + 2}
        width={20}
        height={14}
        rx={4}
        transform={BUTTON_ROTATE}
      />
      <circle className="tmr-bezel" cx={cx} cy={cy} r={r + 8} />
      <circle className="tmr-dial" cx={cx} cy={cy} r={r} />
      <path className="tmr-wedge" d={wedgePath(cx, cy, r - 18, progress)} />
      {TICKS.map((t, i) => (
        <line
          key={i}
          className="tmr-tick"
          x1={t.from.x}
          y1={t.from.y}
          x2={t.to.x}
          y2={t.to.y}
          strokeWidth={t.width}
        />
      ))}
      <circle className="tmr-ink" cx={cx} cy={cy} r={10} />
      <circle className="tmr-gold" cx={cx} cy={cy} r={4} />
      <rect className="tmr-band" {...band} rx={12} />
    </svg>
  );
}

/** The stopwatch sticker. Without `controls` it is the viewer face. */
export function TimerFace({
  view,
  controls,
}: {
  view: TimerView;
  controls?: TimerControls;
}) {
  const { phase } = view;
  const idle = phase === "ready" || phase === "paused";
  const label = phase === "done" ? "Time’s up" : view.digits;

  let digits: React.ReactNode;
  if (controls && idle && controls.draft != null) {
    digits = (
      <input
        className="tmr-digits nodrag"
        aria-label="Duration"
        value={controls.draft}
        autoFocus
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => controls.onDraftChange?.(e.currentTarget.value)}
        onBlur={() => controls.onDraftCommit?.()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") controls.onDraftCommit?.();
          if (e.key === "Escape") controls.onDraftCancel?.();
        }}
      />
    );
  } else if (controls && idle) {
    digits = (
      <button
        type="button"
        className="tmr-digits nodrag"
        aria-label={`Set duration, ${label}`}
        onClick={controls.onEditStart}
      >
        {label}
      </button>
    );
  } else {
    digits = (
      <div className="tmr-digits" role="timer">
        {label}
      </div>
    );
  }

  const pill = (
    text: string,
    onClick?: () => void,
    tone: "ink" | "soft" | "rust" = "ink",
  ) => (
    <button
      type="button"
      className={`tmr-pill${tone === "ink" ? "" : ` tmr-pill-${tone}`} nodrag`}
      onClick={onClick}
    >
      {text}
    </button>
  );

  let rows: React.ReactNode = null;
  if (controls) {
    const c = controls;
    const chips = (
      <div className="tmr-row tmr-chips">
        {DURATION_CHIPS.map((m) => (
          <button
            key={m}
            type="button"
            className="tmr-chip nodrag"
            aria-label={`${m} minutes`}
            aria-pressed={c.durationSeconds === m * 60}
            onClick={() => c.onChip?.(m)}
          >
            {m}
          </button>
        ))}
      </div>
    );
    if (phase === "ready")
      rows = (
        <>
          {chips}
          <div className="tmr-row">{pill("Start", c.onStart)}</div>
        </>
      );
    else if (phase === "paused")
      rows = (
        <>
          {chips}
          <div className="tmr-row">
            {pill("Reset", c.onReset, "soft")}
            {pill("Resume", c.onStart)}
          </div>
        </>
      );
    else
      rows = (
        <div className="tmr-row">
          {pill("+1 min", c.onAddMinute, "soft")}
          {pill("Reset", c.onReset, "soft")}
          {phase === "done"
            ? pill("Restart", c.onStart, "rust")
            : pill("Pause", c.onPause)}
        </div>
      );
  }

  return (
    <div className="tmr" data-state={phase}>
      <div className="tmr-face">
        <Dial progress={view.progress} />
        {digits}
      </div>
      {rows}
    </div>
  );
}

/** A new timer's face, as the picker shows it. */
export function TimerPreview() {
  return (
    <TimerFace
      view={deriveView(INITIAL_TIMER, 0)}
      controls={{ durationSeconds: INITIAL_TIMER.durationSeconds }}
    />
  );
}
